import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  IconButton,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ImageIcon from "@mui/icons-material/Image";
import VisibilityIcon from "@mui/icons-material/Visibility";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import useGetProductReviews from "../../hooks/reviews/useGetProductReviews";
import useToggleReviewVisibility from "../../hooks/reviews/useToggleReviewVisibility";
import Spinner from "../../components/spinner";
import Errors from "../errors";
import Pagination from "../../components/elements/pagination";

const ReviewsList = () => {
  const { id } = useParams();
  const {
    data,
    handlePageClick,
    handleSearchVal,
    handleSetLimit,
    paramsObject,
  } = useGetProductReviews(id);
  const { toggleId, handleToggleVisibility, isLoading } =
    useToggleReviewVisibility(id);
  const navigate = useNavigate();

  // State for modals
  const [selectedReview, setSelectedReview] = useState(null);
  const [selectedImages, setSelectedImages] = useState(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [imagesModalOpen, setImagesModalOpen] = useState(false);

  // Function to handle opening review modal
  const handleOpenReviewModal = (review) => {
    setSelectedReview(review);
    setReviewModalOpen(true);
  };

  // Function to handle opening images modal
  const handleOpenImagesModal = (images) => {
    setSelectedImages(images);
    setImagesModalOpen(true);
  };

  // Render star rating
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        i < rating ? (
          <StarIcon key={i} className="text-yellow-400" />
        ) : (
          <StarBorderIcon key={i} className="text-gray-400" />
        )
      );
    }
    return <div className="flex">{stars}</div>;
  };

  return (
    <div className="category px-4">
      <div className="flex items-center space-x-4 headingBorder__b">
        <div>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
        </div>
        <div>
          <h1 className="heading">Product Reviews</h1>
        </div>
      </div>

      <div className="category_table py-5 space-y-5">
        {/* Controls Row */}
        <div className="flex items-end space-x-4 mb-4">
          <div className="flex-1">
            <input
              onChange={handleSearchVal}
              value={paramsObject.searchQuery}
              type="text"
              placeholder="Search in reviews..."
              className="input"
            />
          </div>
          <div>
            <label htmlFor="" className="label">
              Limit
            </label>
            <select
              name="limit"
              value={data.data?.per_page || paramsObject.limit}
              className="input"
              onChange={handleSetLimit}
            >
              {Array(5)
                .fill(1)
                .map((_, idx) => {
                  return (
                    <option key={idx} value={(idx + 1) * 10}>
                      {(idx + 1) * 10}
                    </option>
                  );
                })}
            </select>
          </div>
        </div>

        {/* Table */}
        {data?.isLoading || data?.isFetching ? (
          <Spinner />
        ) : data?.status === "error" ? (
          <Errors errorObject={data.error} inline />
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg shadow overflow-y-auto relative">
            <table className="border-collapse table-auto w-full whitespace-no-wrap bg-white table-striped relative">
              <thead>
                <tr className="text-left">
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                    Sr No
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                    <p className="w-40">Customer Name</p>
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                    Rating
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                    Review
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                    Date
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center">
                    <p className="w-44">Images</p>
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center">
                    Visibility
                  </th>
                </tr>
              </thead>
              <tbody>
                {data?.data?.data?.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center py-8 text-gray-500 font-medium"
                    >
                      No Reviews Found
                    </td>
                  </tr>
                ) : (
                  data?.data?.data?.map((review, idx) => {
                    const {
                      rating_id,
                      rating,
                      review: reviewText,
                      created_on,
                      name,
                      review_images,
                      status, // <-- get status from API
                    } = review || {};

                    return (
                      <tr key={rating_id || idx}>
                        <td className="border-dashed border-t border-gray-200 px-6 py-2 text-sm">
                          {paramsObject.limit * paramsObject.pageNo + idx + 1}
                        </td>
                        <td className="border-dashed border-t border-gray-200 px-6 py-2 text-sm">
                          {name || "Anonymous"}
                        </td>
                        <td className="border-dashed border-t border-gray-200 px-6 py-2">
                          {renderStars(rating)}
                        </td>
                        <td className="border-dashed border-t border-gray-200 px-6 py-2">
                          <div className="flex items-center space-x-2">
                            <div className="truncate max-w-xs">
                              {reviewText?.length > 30
                                ? reviewText.substring(0, 50) + "..."
                                : reviewText}
                            </div>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenReviewModal(review)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </div>
                        </td>
                        <td className="border-dashed border-t border-gray-200 px-6 py-2 text-sm">
                          {new Date(created_on).toLocaleDateString()}
                        </td>
                        <td className="border-dashed border-t border-gray-200 px-6 py-2 text-center">
                          {review_images && review_images.length > 0 ? (
                            <Button
                              startIcon={<ImageIcon />}
                              variant="text"
                              size="small"
                              onClick={() =>
                                handleOpenImagesModal(review_images)
                              }
                            >
                              {review_images.length}{" "}
                              {review_images.length === 1 ? "Image" : "Images"}
                            </Button>
                          ) : (
                            <span className="text-gray-400">No Images</span>
                          )}
                        </td>
                        <td className="border-dashed border-t border-gray-200 px-6 py-2 text-center">
                          <div className="flex items-center justify-center">
                            {rating_id === toggleId && isLoading ? (
                              <Spinner className={"w-8 h-8"} />
                            ) : (
                              <>
                                <span className="text-gray-700 text-sm">
                                  Hide
                                </span>
                                <Switch
                                  inputProps={{ "aria-label": "controlled" }}
                                  onChange={(e) =>
                                    handleToggleVisibility(
                                      rating_id,
                                      e.target.checked
                                    )
                                  }
                                  className="hide_show_switch"
                                  checked={!!status}
                                />
                                <span className="text-gray-700 text-sm">
                                  Show
                                </span>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div>
          <Pagination
            currentPage={+paramsObject.pageNo}
            lengthofItems={data?.data?.total_count}
            limit={paramsObject.limit}
            onPageChange={handlePageClick}
            pageCount={Math.ceil(
              (data?.data?.total_count || 0) / (data?.data?.per_page || 10)
            )}
          />
        </div>
      </div>

      {/* Review Modal */}
      <Dialog
        open={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: { borderRadius: 16, padding: "8px 0" },
        }}
      >
        <DialogTitle>
          <div className="flex items-center space-x-3">
            <Avatar>
              {selectedReview?.name ? selectedReview.name[0] : "?"}
            </Avatar>
            <div>
              <div className="font-semibold">
                {selectedReview?.name || "Anonymous"}
              </div>
              <div className="text-xs text-gray-500">
                {selectedReview &&
                  new Date(selectedReview.created_on).toLocaleString()}
              </div>
            </div>
            <div className="ml-auto">
              {selectedReview && renderStars(selectedReview.rating)}
            </div>
          </div>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedReview && (
            <div className="space-y-4 py-2">
              <div>
                <span className="font-medium text-gray-700">Review:</span>
                <p className="mt-2 text-base text-gray-900 leading-relaxed">
                  {selectedReview.review}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReviewModalOpen(false)}
            variant="outlined"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Images Modal */}
      <Dialog
        open={imagesModalOpen}
        onClose={() => setImagesModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: { borderRadius: 16, padding: "8px 0" },
        }}
      >
        <DialogTitle>
          <div className="flex items-center justify-between">
            <span>Review Images</span>
            <span className="text-xs text-gray-500">
              {selectedImages?.length || 0} image
              {selectedImages?.length === 1 ? "" : "s"}
            </span>
          </div>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedImages && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-2">
              {selectedImages.map((image, index) => (
                <div
                  key={image.image_id || index}
                  className="aspect-square overflow-hidden rounded-lg"
                >
                  <img
                    src={image.image_url}
                    alt={`Review ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setImagesModalOpen(false)}
            variant="outlined"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ReviewsList;
